import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import { LABELS_ROLES, ROLES_DELEGANTS, ROLES_ANALYTICS } from '../constants/roles';
import { IconeCompte, IconeCle, IconeArchive, IconePlume, IconeGraphique } from './IconesSVG';

// Menu de compte de l'entete : l'avatar ouvre les actions liees a l'utilisateur.
// Regroupe ici ce qui etait dissemine en boutons isoles (cle, archives,
// deconnexion), dont l'icone cle que personne n'associait au mot de passe.
// `sens` : 'bas' pour un avatar en barre superieure, 'haut' pour un avatar en
// bas de barre laterale — sinon le menu s'ouvrirait hors de l'ecran.
export default function MenuProfil({ couleurAvatar = '#C0392B', sens = 'bas', libelle }) {
  const navigate = useNavigate();
  const emplacement = useLocation();
  const { user, logout } = useAuthStore();
  const [ouvert, setOuvert] = useState(false);
  const conteneur = useRef(null);

  const roleUser = user?.roles?.[0]?.role || user?.role;
  const filiale = user?.roles?.[0]?.filiale?.nom;
  const initiales = (user?.prenom?.[0] || '') + (user?.nom?.[0] || '');

  // Fermeture au clic exterieur. `mousedown` plutot que `click` : le menu doit
  // se fermer des l'appui, avant qu'un eventuel clic ne parte ailleurs.
  useEffect(() => {
    if (!ouvert) return undefined;
    const auClic = (e) => {
      if (conteneur.current && !conteneur.current.contains(e.target)) setOuvert(false);
    };
    const auClavier = (e) => { if (e.key === 'Escape') setOuvert(false); };
    document.addEventListener('mousedown', auClic);
    document.addEventListener('keydown', auClavier);
    return () => {
      document.removeEventListener('mousedown', auClic);
      document.removeEventListener('keydown', auClavier);
    };
  }, [ouvert]);

  // Fermeture a la navigation : sans cela le menu resterait ouvert par-dessus
  // la page suivante.
  useEffect(() => { setOuvert(false); }, [emplacement.pathname]);

  const allerA = (chemin) => { setOuvert(false); navigate(chemin); };

  const seDeconnecter = () => {
    setOuvert(false);
    if (window.confirm('Voulez-vous vraiment vous déconnecter ?')) logout();
  };

  return (
    <div ref={conteneur} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setOuvert((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={ouvert}
        aria-label="Menu du compte"
        title={(user?.prenom || '') + ' ' + (user?.nom || '')}
        style={{ ...s.avatar, background: couleurAvatar }}
      >
        {initiales || <IconeCompte taille={18} />}
      </button>
      {libelle && (
        <div className="tronque" style={s.libelle} onClick={() => setOuvert((o) => !o)}>{libelle}</div>
      )}

      {ouvert && (
        <div role="menu" style={{ ...s.menu, ...(sens === 'haut' ? s.menuHaut : s.menuBas) }}>
          <div style={s.entete}>
            <div style={{ ...s.avatarMenu, background: couleurAvatar }}>{initiales}</div>
            <div style={{ minWidth: 0 }}>
              <div className="tronque" style={s.nom}>{user?.prenom} {user?.nom}</div>
              <div className="tronque" style={s.role}>{LABELS_ROLES[roleUser] || roleUser}</div>
              {filiale && <div className="tronque" style={s.filiale}>{filiale}</div>}
            </div>
          </div>

          <div style={s.separateur} />

          <button type="button" role="menuitem" style={s.item} onClick={() => allerA('/archives')}>
            <IconeArchive taille={15} /> Archives
          </button>
          <button type="button" role="menuitem" style={s.item} onClick={() => allerA('/changer-mot-de-passe')}>
            <IconeCle taille={15} /> Changer mon mot de passe
          </button>
          {ROLES_ANALYTICS.includes(roleUser) && (
            <button type="button" role="menuitem" style={s.item} onClick={() => allerA('/analyses')}>
              <IconeGraphique taille={15} /> Analyses
            </button>
          )}
          {/* Deleguer sa signature n'a de sens que pour ceux qui signent. */}
          {ROLES_DELEGANTS.includes(roleUser) && (
            <button type="button" role="menuitem" style={s.item} onClick={() => allerA('/delegations')}>
              <IconePlume taille={15} /> Mes délégations
            </button>
          )}

          <div style={s.separateur} />

          <button type="button" role="menuitem" style={{ ...s.item, color: '#C0392B' }} onClick={seDeconnecter}>
            Déconnexion
          </button>
        </div>
      )}
    </div>
  );
}

const s = {
  avatar: {
    width: 38, height: 38, minWidth: 38, borderRadius: '50%', border: 'none', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 13, fontWeight: 700, color: '#fff', fontFamily: 'inherit', padding: 0,
  },
  menu: {
    position: 'absolute', zIndex: 1500,
    width: 260, maxWidth: 'calc(100vw - 24px)',
    background: '#fff', borderRadius: 12, border: '0.5px solid #e5e7eb',
    boxShadow: '0 12px 32px rgba(0,0,0,0.18)', overflow: 'hidden',
    fontFamily: 'inherit', textAlign: 'left',
  },
  menuBas: { top: 'calc(100% + 8px)', right: 0 },
  menuHaut: { bottom: 'calc(100% + 8px)', left: 0 },
  entete: { display: 'flex', alignItems: 'center', gap: 10, padding: '14px 14px 12px' },
  avatarMenu: {
    width: 40, height: 40, minWidth: 40, borderRadius: '50%', display: 'flex',
    alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, color: '#fff',
  },
  nom: { fontSize: 14, fontWeight: 700, color: '#0D1B2A' },
  role: { fontSize: 12, color: '#666', marginTop: 1 },
  filiale: { fontSize: 11, color: '#8899AA', marginTop: 1 },
  libelle: { fontSize: 11, color: '#8899AA', marginTop: 6, cursor: 'pointer', maxWidth: 150 },
  separateur: { height: 1, background: '#f0f0f0' },
  item: {
    display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 44,
    padding: '10px 14px', border: 'none', background: 'transparent',
    fontSize: 13, color: '#0D1B2A', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
  },
};
